import { CommonModule } from '@angular/common';
import {
  ConnectedPosition,
  Overlay,
  OverlayModule,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { Subscription } from 'rxjs';

/**
 * Picker emoji du chat — émet l'UNICODE BRUT (isNative, contrat T1/T4).
 * Le popup est rendu via un overlay CDK (attaché au body, comme mat-menu) :
 * rendu inline il restait piégé sous le mat-drawer (stacking contexts) et
 * clippé par les conteneurs scrollables. Le picker lui-même est dans un bloc
 * @defer : il ne charge qu'à la première ouverture (hors bundle initial du
 * module chat). picker.css est déjà global (angular.json).
 */
@Component({
  selector: 'chat-emoji-picker',
  standalone: true,
  imports: [CommonModule, OverlayModule, PickerModule, MatButtonModule, MatIconModule],
  template: `
    <button
      #trigger
      mat-icon-button
      type="button"
      (click)="toggled()"
      aria-label="emoji"
      [disabled]="disabled"
    >
      <mat-icon class="secondary-text">sentiment_satisfied_alt</mat-icon>
    </button>

    <ng-template #popupTpl>
      <div class="chat-emoji-picker-popup">
        @defer (when isOpened) {
          <emoji-mart
            [isNative]="true"
            [showPreview]="false"
            [emojiSize]="20"
            title=""
            (emojiSelect)="onSelect($event)"
          ></emoji-mart>
        } @placeholder {
          <div class="chat-emoji-picker-loading">…</div>
        }
      </div>
    </ng-template>
  `,
  styles: [
    `
      .chat-emoji-picker-popup {
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        border-radius: 8px;
      }
      .chat-emoji-picker-loading {
        width: 338px;
        height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
      }
    `,
  ],
})
export class ChatEmojiPickerComponent implements OnDestroy {
  @Input() disabled = false;
  @Input() direction: 'up' | 'down' = 'up';
  @Output() emojiSelected = new EventEmitter<string>();

  public isOpened = false;

  @ViewChild('trigger', { read: ElementRef }) trigger?: ElementRef<HTMLElement>;
  @ViewChild('popupTpl') popupTpl?: TemplateRef<unknown>;

  private overlayRef: OverlayRef | null = null;
  private outsideSub: Subscription | null = null;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
  ) {}

  public toggled(): void {
    if (this.overlayRef) {
      this.close();
      return;
    }
    if (!this.trigger || !this.popupTpl) {
      return;
    }

    const up: ConnectedPosition[] = [
      { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
      { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
    ];
    const down: ConnectedPosition[] = [
      { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
      { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
    ];

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.trigger)
        .withPositions(this.direction === 'down' ? down : up)
        .withPush(true),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    this.isOpened = true;
    this.overlayRef.attach(new TemplatePortal(this.popupTpl, this.viewContainerRef));
    this.outsideSub = this.overlayRef.outsidePointerEvents().subscribe((event) => {
      if (!this.trigger?.nativeElement.contains(event.target as Node)) {
        this.close();
      }
    });
  }

  public onSelect(event: { emoji?: { native?: string } }): void {
    const native = event?.emoji?.native ?? '';
    if (native) {
      this.emojiSelected.emit(native);
    }
    this.close();
  }

  private close(): void {
    this.isOpened = false;
    this.outsideSub?.unsubscribe();
    this.outsideSub = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  ngOnDestroy(): void {
    this.close();
  }
}
